/**
 * @fileoverview Device Tokens API functions
 * 
 * Handles push notification device token management including:
 * - Adding device tokens
 * - Checking device tokens
 * 
 * @module api/device-tokens
 */

import apiClient, { 
  handleUnauthorizedResponse 
} from './client';

// ============================================================================
// DEVICE TOKEN MANAGEMENT
// ============================================================================

/**
 * Add a device token for push notifications
 * @param {string} restaurantId - Restaurant ID
 * @param {string} token - Device token
 * @param {string} browser - Browser name
 * @returns {Promise} Add token response
 */
export const addDeviceToken = async (restaurantId, token, browser) => {
  try {
    const payload = {
      restaurant_id: restaurantId,
      token: token,
      browser: browser,
    };

    const response = await apiClient.post('/device-tokens/', payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      // The backend returns 400 "Device token already exists" when this exact
      // token is already registered — that is a benign no-op, not an error. Match
      // it loosely (the detail wording has varied) so it never logs as an error.
      const detail = error.response.data?.detail;
      if (
        error.response.status === 400 &&
        typeof detail === 'string' &&
        detail.toLowerCase().includes('already exists')
      ) {
        console.warn('Device token already registered for this restaurant.');
        return { message: 'Device token already exists' };
      } else if (error.response.status === 401) {
        handleUnauthorizedResponse();
      }
      console.error('Error adding device token:', error.response.data);
    } else {
      console.error('Error adding device token:', error.message);
    }
    throw error;
  }
};

/**
 * De-register a device token (called on logout so this device stops receiving
 * push notifications). Best-effort and time-boxed — logout must never hang on
 * it, and a missing token on the server is a benign no-op.
 * @param {string} token - FCM device token to remove
 * @returns {Promise} Remove token response
 */
export const removeDeviceToken = async (token) => {
  try {
    const response = await apiClient.delete('/device-tokens/', {
      params: { token },
      timeout: 8000,
    });
    return response.data;
  } catch (error) {
    console.error('Error removing device token:', error.response?.data || error.message);
    throw error;
  }
};

